import React from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: "primary" | "secondary" | "outline" | "ghost" | "danger" | "success";
    size?: "sm" | "md" | "lg";
    loading?: boolean;
    icon?: React.ReactNode;
}

export function Button({
    children,
    variant = "primary",
    size = "md",
    className = "",
    loading = false,
    icon,
    disabled,
    ...props
}: ButtonProps) {
    const baseStyles = `
        inline-flex items-center justify-center gap-2 rounded-lg font-semibold
        transition-all duration-200 ease-out
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
        disabled:pointer-events-none disabled:opacity-50
        active:scale-[0.98]
    `;

    const variants = {
        primary: `
            bg-[#1e3a8a] text-white
            hover:bg-blue-900
            shadow-sm
            focus-visible:ring-blue-900
        `,
        secondary: `
            bg-white text-slate-700 border border-slate-300
            hover:bg-slate-50
            dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600
            dark:hover:bg-slate-700
            focus-visible:ring-slate-500
        `,
        outline: `
            bg-white text-slate-700 border border-slate-300
            hover:bg-slate-50
            dark:bg-transparent dark:text-blue-400 dark:border-blue-500
            dark:hover:bg-slate-800
            focus-visible:ring-slate-500
        `,
        ghost: `
            text-blue-700 bg-transparent
            hover:bg-blue-50 hover:text-blue-900
            dark:text-blue-400 dark:hover:bg-slate-800 dark:hover:text-blue-300
            focus-visible:ring-blue-500
        `,
        danger: `
            bg-red-600 text-white
            hover:bg-red-700
            shadow-sm
            focus-visible:ring-red-600
        `,
        success: `
            bg-emerald-600 text-white
            hover:bg-emerald-700
            shadow-sm
            focus-visible:ring-emerald-600
        `,
    };

    const sizes = {
        sm: "h-9 px-4 text-sm",
        md: "h-11 px-6 text-sm",
        lg: "h-12 px-8 text-base",
    };

    return (
        <button
            className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
            disabled={disabled || loading}
            {...props}
        >
            {loading ? (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
            ) : icon ? (
                icon
            ) : null}
            {children}
        </button>
    );
}

// Icon Button variant
interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: "primary" | "outline" | "ghost";
    size?: "sm" | "md" | "lg";
}

export function IconButton({
    children,
    variant = "ghost",
    size = "md",
    className = "",
    ...props
}: IconButtonProps) {
    const baseStyles = `
        inline-flex items-center justify-center rounded-lg
        transition-all duration-200
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
        disabled:pointer-events-none disabled:opacity-50
        active:scale-95
    `;

    const variants = {
        primary: "bg-blue-900 text-white hover:bg-blue-800 focus-visible:ring-blue-900",
        outline: "border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200 focus-visible:ring-slate-500",
        ghost: "text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200 focus-visible:ring-slate-500",
    };

    const sizes = {
        sm: "h-8 w-8",
        md: "h-10 w-10",
        lg: "h-12 w-12",
    };

    return (
        <button
            className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
            {...props}
        >
            {children}
        </button>
    );
}
